// src/tabs/messages/SmsTransversalView.jsx — vue SMS transversale (Lot O4 +
// éditeur SMS, disposition 3 COLONNES).
//
// Décision Dimitri (« la même liberté d'édition sur les SMS que sur mes
// emails ») : les gabarits SMS sont ÉDITABLES ici — corps stocké en base
// (table sms_templates), texte CODÉ en fallback tant que rien n'est édité
// (src/lib/sms-catalog.js, miroir contrôlé de lib/sms-templates.js +
// lib/marketing-sms.js). Écriture UNIQUEMENT via l'action serveur
// `sms_template_save` (requireAdmin + re-validation serveur) ; le validateur
// client (src/lib/sms-validator.js, MÊME module que le serveur) n'est qu'un
// confort : compteur pire-cas live + erreurs inline avant d'appuyer sur
// Enregistrer.
//
// DISPOSITION (décision Dimitri post-démo) : la structure 3 colonnes EXACTE de
// l'onglet Direct (DirectMessagesView.jsx) :
//   • Colonne GAUCHE  — liste : état du canal + les 5 gabarits SMS
//     (sélectionnables, badge « Édité ») ;
//   • Colonne CENTRE  — l'éditeur du gabarit sélectionné (textarea FR/EN,
//     compteur pire-cas, erreurs/warnings, Enregistrer / reset inline) ;
//   • Colonne DROITE  — aperçu du SMS rendu (variables développées en valeurs
//     d'exemple + compte caractères/segments du texte d'exemple), sticky.
// Responsive identique à Direct : < 768 tout empilé ; < 1180 éditeur + aperçu
// empilés. Les sections annexes (envois programmés, journal) restent SOUS les
// 3 colonnes (comme Direct, dont la colonne gauche ne porte que la liste).
//
// L'édition ne touche AUCUN verrou d'envoi : canal Twilio DORMANT
// (enabled:false), MESSAGES_DRY_RUN, opt-in/opt-out inchangés.
//
// RGPD : le journal n'affiche que des ids techniques tronqués — jamais de
// numéro de téléphone ni de nom.

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  MessageSquareText,
  ShieldCheck,
  Megaphone,
  AlertTriangle,
  PowerOff,
  Power,
  Save,
  RotateCcw,
  History,
  Eye,
  Smartphone,
  Languages,
  RefreshCw,
  Sparkles,
  Plus,
} from "lucide-react";
import { supabase } from "../../supabase";
import { DS } from "../../lib/ui";
import { useIsMobile } from "../../lib/useIsMobile";
import { INBOX_CHANNELS } from "../../lib/inbox-channels";
import { SMS_CATALOG } from "../../lib/sms-catalog";
import {
  validateSmsTemplate,
  computeWorstCase,
  SMS_SEGMENT_MAX,
  gsm7Septets,
  segmentsForSeptets,
} from "../../lib/sms-validator";
import {
  fetchSmsTemplatesMap,
  saveSmsTemplate,
} from "../../lib/sms-templates-api";
import { SMS_SEND_COLUMNS } from "../../lib/sms-sends-api";
import { getAllLogsFlat } from "../../lib/sousloc-helpers";
import SmsSendCard from "./SmsSendCard";
// Résumé humanisé d'un send — RÉUTILISÉ tel quel (le canal « SMS » est dit via
// la table de libellés partagée textes-auto-labels.js).
import { humanSendSummary, isEngineActiveStatus } from "./MessageEditor";
// Libellés d'affichage des statuts d'envoi (journal) — table unique.
import { sendStatusLabel } from "./textes-auto-labels";

const KIND_TAG = {
  transactionnel: {
    label: "Lié au séjour",
    bg: DS.cielSoft,
    fg: DS.marine,
    Icon: ShieldCheck,
  },
  marketing: {
    label: "Publicité",
    bg: DS.corailSoft,
    fg: DS.corailText,
    Icon: Megaphone,
  },
};

// Nom lisible d'un modèle SMS du journal : libellé du catalogue, sinon
// l'identifiant brut (un envoi hors catalogue reste identifiable).
function smsTemplateName(slug) {
  if (!slug) return "—";
  const entry = SMS_CATALOG.find((t) => t.slug === slug);
  return entry ? entry.label : slug;
}

const ORIGIN_LABEL = {
  direct: "Parcours Direct",
  ota: "Après séjour Airbnb / Booking",
};

// ── Valeurs d'EXEMPLE pour l'aperçu (client, affichage seul) ─────────────────
// Même pattern que l'aperçu Direct (interpolatePreview + exampleVars) : chaque
// {token} connu est remplacé par une valeur réaliste ; un token inconnu reste
// littéral (comme au runtime SMS — aucune variable n'y est filtrée à l'envoi).
// Les liens d'exemple ont la FORME réelle (base prod sejour.fullbnbzen.com,
// /s/<12 hex> parcours = 44 car, /r/<code8> = 40 car — registre SMS_VAR_MAX_LEN).
const SMS_EXAMPLE_VARS = {
  prenom: "Camille",
  logement: "Cocon Naturel",
  lien: "https://sejour.fullbnbzen.com/s/a1b2c3d4e5f6",
  lien_stop: "https://sejour.fullbnbzen.com/r/x9y8z7w6",
  code: "CAMILLE10",
};

// Interpolation d'aperçu : {token} connu → valeur d'exemple, inconnu → littéral.
// Sert UNIQUEMENT à l'aperçu — le contenu stocké garde toujours les {tokens}.
function interpolateSmsPreview(text) {
  if (typeof text !== "string") return "";
  return text.replace(/\{(\w+)\}/g, (whole, tok) => {
    const v = SMS_EXAMPLE_VARS[tok];
    return v == null ? whole : String(v);
  });
}

function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontFamily: DS.fontDisplay,
        fontSize: 14.5,
        fontWeight: 600,
        color: DS.marineDark,
        margin: "18px 0 8px",
      }}
    >
      {children}
    </div>
  );
}

// ── Habillage : DA reprise des éditeurs Direct (DirectMessagesView.jsx) et
// Plateformes (MessageEditor.jsx) — décision Dimitri post-démo : même identité
// visuelle que Communication → Template, pas un design à part. ──────────────

// Sous-libellé de champ (SubLabel de DirectMessagesView).
function SubLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: DS.textMuted,
        margin: "0 0 6px",
      }}
    >
      {children}
    </div>
  );
}

// Badge « Édité » — style EditedBadge de DirectMessagesView. Le data-testid
// (sms-edited-badge-<slug>) n'est posé QUE sur l'exemplaire de la LISTE
// (unicité testid) ; l'éditeur peut en rendre un exemplaire purement visuel.
function EditedBadge({ testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.3,
        padding: "3px 7px",
        borderRadius: 5,
        textTransform: "uppercase",
        background: DS.cielSoft,
        color: DS.marineDeep,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
      }}
    >
      <History size={11} strokeWidth={2} /> Édité
    </span>
  );
}

// Compteur pire-cas live (X/160 septets, segments). Rouge si non-GSM-7,
// ambre si > 160 (avertissement segments), discret sinon.
function WorstCaseCounter({ slug, value, lang, testId }) {
  const wc = computeWorstCase(slug, value);
  let text;
  let color = DS.textMuted;
  if (!wc.gsm7) {
    text = "caractère refusé par les SMS — voir le détail";
    color = DS.dangerText;
  } else {
    text = `jusqu'à ${wc.length}/${SMS_SEGMENT_MAX} caractères · ${wc.segments} SMS facturé${wc.segments > 1 ? "s" : ""}`;
    if (wc.length > SMS_SEGMENT_MAX) color = DS.warningText;
  }
  return (
    <div
      data-testid={testId}
      style={{ fontSize: 11, color, marginTop: 4, textAlign: "right" }}
    >
      {lang ? `${lang} — ${text}` : text}
    </div>
  );
}

// Erreurs (bloquantes) + avertissements : encarts pleine largeur, mêmes styles
// que direct-msg-save-error (danger) et direct-msg-en-partial (warning).
function ValidationFeedback({ errors, warnings, testId }) {
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <div data-testid={testId}>
      {errors.length > 0 && (
        <div
          style={{
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: DS.radiusControl,
            padding: "9px 12px",
            marginTop: 9,
            fontSize: 12.5,
            lineHeight: 1.45,
          }}
        >
          {errors.map((e, i) => (
            <div key={`e-${i}`}>
              {e.lang ? `[${e.lang.toUpperCase()}] ` : ""}
              {e.message}
            </div>
          ))}
        </div>
      )}
      {warnings.length > 0 && (
        <div
          style={{
            marginTop: 9,
            border: `1px solid ${DS.warning}`,
            background: DS.warningBg,
            color: DS.warningText,
            borderRadius: 8,
            padding: "8px 11px",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.45,
          }}
        >
          {warnings.map((w, i) => (
            <div key={`w-${i}`}>
              {w.lang ? `[${w.lang.toUpperCase()}] ` : ""}
              {w.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Colonne GAUCHE : ligne de gabarit (carte de liste, pattern MessageRow de
// DirectMessagesView — sélection = contour coloré + surélévation, fond blanc,
// jamais de fond de couleur). ────────────────────────────────────────────────
function SmsTemplateRow({ t, edited, selected, onSelect }) {
  const tag = KIND_TAG[t.kind] || KIND_TAG.transactionnel;
  const TagIcon = tag.Icon;
  return (
    <div
      data-testid={`sms-gabarit-${t.slug}`}
      className="fbz-press"
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 9px",
        borderRadius: DS.radiusControl,
        cursor: "pointer",
        background: DS.card,
        border: `1.5px solid ${selected ? DS.marine : "transparent"}`,
        boxShadow: selected ? "0 7px 18px -8px rgba(20,51,80,.35)" : "none",
        marginBottom: 3,
      }}
    >
      <MessageSquareText
        size={15}
        strokeWidth={2}
        color={DS.marine}
        style={{ flexShrink: 0 }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: DS.fontDisplay,
            fontWeight: 600,
            fontSize: 13.5,
            color: DS.marineDark,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t.label}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 3,
          }}
        >
          <span
            style={{
              background: tag.bg,
              color: tag.fg,
              borderRadius: 8,
              padding: "1px 7px",
              fontSize: 10,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <TagIcon size={10} strokeWidth={2.4} />
            {tag.label}
          </span>
          <span style={{ fontSize: 10, color: DS.textMuted }}>
            {ORIGIN_LABEL[t.origin] || t.origin}
          </span>
        </div>
      </span>
      {edited && <EditedBadge testId={`sms-edited-badge-${t.slug}`} />}
    </div>
  );
}

// ── Bloc TRADUCTIONS (lecture seule) : EN + ES générées AU SAVE ──────────────
// Décision Dimitri : « l'admin n'édite QUE le français ». Les corps EN et ES
// sont GÉNÉRÉS par le serveur au moment de l'enregistrement (Claude Haiku +
// post-validation GSM-7/160/variables) — jamais saisis à la main. On les rend
// en LECTURE SEULE ; « Régénérer » relance la traduction sur le FR courant.
// Une traduction indisponible (échec API / rejet post-validation) → badge
// « traduction indisponible — fallback FR » (le runtime retombe sur la chaîne
// codée es→en→fr, cf. renderer pickDbBody). Le FR se sauve TOUJOURS, même si
// les deux traductions échouent.
function TranslationRow({ langLabel, body, placeholder, showBadge, testId, badgeTestId }) {
  const hasBody = typeof body === "string" && body.trim() !== "";
  return (
    <div style={{ marginTop: 10 }}>
      <SubLabel>{langLabel}</SubLabel>
      <div
        data-testid={testId}
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontSize: 13,
          lineHeight: 1.5,
          fontFamily: DS.fontBody,
          color: hasBody ? DS.textPrimary : DS.textMuted,
          padding: "10px 12px",
          borderRadius: DS.radiusControl,
          border: `0.5px dashed ${DS.border}`,
          background: DS.pageBg,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          minHeight: 38,
          fontStyle: hasBody ? "normal" : "italic",
        }}
      >
        {hasBody ? body : placeholder}
      </div>
      {showBadge && (
        <div
          data-testid={badgeTestId}
          style={{
            marginTop: 5,
            fontSize: 11,
            fontWeight: 700,
            color: DS.warningText,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <AlertTriangle size={12} strokeWidth={2.2} /> traduction indisponible —
          le français sera envoyé
        </div>
      )}
    </div>
  );
}

function TranslationsPanel({ t, enBody, esBody, enShowBadge, esShowBadge, onRegenerate, regenerating, canRegenerate }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: DS.marine,
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          <Languages size={14} strokeWidth={2} /> Traductions générées
          automatiquement
        </span>
        <button
          type="button"
          className="fbz-press"
          data-testid={`sms-regenerate-${t.slug}`}
          onClick={onRegenerate}
          disabled={!canRegenerate}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: DS.fontBody,
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 11px",
            borderRadius: DS.radiusControl,
            border: `1px solid ${DS.border}`,
            background: DS.card,
            color: DS.marine,
            cursor: canRegenerate ? "pointer" : "not-allowed",
            opacity: canRegenerate ? 1 : 0.5,
          }}
        >
          <RefreshCw size={13} strokeWidth={2} />
          {regenerating ? "Régénération…" : "Régénérer"}
        </button>
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: DS.textMuted,
          lineHeight: 1.45,
          margin: "0 0 4px",
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <Sparkles size={11} strokeWidth={2} /> Vous écrivez en français ;
        l'anglais et l'espagnol sont traduits et vérifiés automatiquement quand
        vous enregistrez.
      </div>
      <TranslationRow
        langLabel="Anglais (EN) — lecture seule"
        body={enBody}
        placeholder="Générée à l'enregistrement du français."
        showBadge={enShowBadge}
        testId={`sms-generated-en-${t.slug}`}
        badgeTestId={`sms-translation-badge-en-${t.slug}`}
      />
      <TranslationRow
        langLabel="Espagnol (ES) — lecture seule"
        body={esBody}
        placeholder="Générée à l'enregistrement du français."
        showBadge={esShowBadge}
        testId={`sms-generated-es-${t.slug}`}
        badgeTestId={`sms-translation-badge-es-${t.slug}`}
      />
    </div>
  );
}

// ── Colonne CENTRE : éditeur du gabarit sélectionné ──────────────────────────
// Le FR est le SEUL champ éditable (textarea + compteur pire-cas live +
// erreurs/warnings du validateur). EN + ES sont GÉNÉRÉS au save (bloc
// TranslationsPanel, lecture seule). Enregistrer (CTA corail), Annuler (retour
// au contenu effectif), « Revenir au texte codé » avec confirmation INLINE
// (pattern direct-msg-reset-confirmbox).
function SmsEditorCard({
  t,
  edited,
  draftFr,
  onChangeFr,
  vFr,
  enBody,
  esBody,
  enShowBadge,
  esShowBadge,
  onRegenerate,
  regenerating,
  liveErrors,
  liveWarnings,
  serverIssue,
  canSave,
  saving,
  confirmReset,
  onSave,
  onCancel,
  onAskReset,
  onCancelReset,
  onConfirmReset,
}) {
  const tag = KIND_TAG[t.kind] || KIND_TAG.transactionnel;
  const TagIcon = tag.Icon;
  return (
    <div
      data-testid={`sms-editor-${t.slug}`}
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: DS.radiusCard,
        boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
        padding: "14px 16px",
      }}
    >
      {/* En-tête : nom + nature + origine + slug (+ badge visuel Édité). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <MessageSquareText size={15} strokeWidth={2} color={DS.marine} />
        <span
          title={t.slug}
          style={{ fontWeight: 700, fontSize: 13, color: DS.marineDark }}
        >
          {t.label}
        </span>
        <span
          style={{
            background: tag.bg,
            color: tag.fg,
            borderRadius: 8,
            padding: "2px 8px",
            fontSize: 10.5,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <TagIcon size={11} strokeWidth={2.4} />
          {tag.label}
        </span>
        <span style={{ fontSize: 10.5, color: DS.textMuted }}>
          {ORIGIN_LABEL[t.origin] || t.origin}
        </span>
        {edited && <EditedBadge />}
      </div>

      {/* Zone message — panneau pageBg + libellé section uppercase, pattern
          « Le message » de l'éditeur Direct (DirectMessagesView). */}
      <div
        style={{
          border: `1px solid ${DS.border}`,
          borderRadius: 12,
          padding: 15,
          background: DS.pageBg,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              color: DS.marine,
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            <MessageSquareText size={14} strokeWidth={2} /> Le message
          </span>
        </div>

        <SubLabel>Corps (FR)</SubLabel>
        <textarea
          data-testid={`sms-edit-fr-${t.slug}`}
          value={draftFr}
          onChange={(e) => onChangeFr(e.target.value)}
          rows={3}
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: 13,
            lineHeight: 1.5,
            fontFamily: DS.fontBody,
            color: DS.textPrimary,
            padding: "10px 12px",
            borderRadius: DS.radiusControl,
            border:
              vFr && !vFr.ok
                ? `1px solid ${DS.danger}`
                : `0.5px solid ${DS.border}`,
            background: DS.card,
            resize: "vertical",
          }}
        />
        <WorstCaseCounter
          slug={t.slug}
          value={draftFr}
          lang="FR"
          testId={`sms-counter-fr-${t.slug}`}
        />

        <TranslationsPanel
          t={t}
          enBody={enBody}
          esBody={esBody}
          enShowBadge={enShowBadge}
          esShowBadge={esShowBadge}
          onRegenerate={onRegenerate}
          regenerating={regenerating}
          canRegenerate={canSave}
        />

        <ValidationFeedback
          errors={liveErrors}
          warnings={liveWarnings}
          testId={`sms-validator-feedback-${t.slug}`}
        />
        {serverIssue && (
          <ValidationFeedback
            errors={serverIssue.errors}
            warnings={serverIssue.warnings}
            testId={`sms-server-feedback-${t.slug}`}
          />
        )}

        {/* Actions — mêmes boutons que l'éditeur Direct : CTA corail
            (Enregistrer), secondaire card/border (Annuler, Revenir au texte
            codé), confirmation reset INLINE (direct-msg-reset-confirmbox). */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="fbz-press"
            data-testid={`sms-save-${t.slug}`}
            onClick={onSave}
            disabled={!canSave}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: DS.fontBody,
              fontSize: 13,
              fontWeight: 600,
              padding: "10px 15px",
              borderRadius: DS.radiusControl,
              border: "1px solid transparent",
              background: DS.corail,
              color: "#fff",
              cursor: canSave ? "pointer" : "not-allowed",
              opacity: canSave ? 1 : 0.5,
            }}
          >
            <Save size={15} strokeWidth={2} />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button
            type="button"
            className="fbz-press"
            data-testid={`sms-cancel-${t.slug}`}
            onClick={onCancel}
            disabled={saving}
            style={{
              border: `1px solid ${DS.border}`,
              background: DS.card,
              color: DS.textSecondary,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: DS.fontBody,
              padding: "9px 14px",
              borderRadius: DS.radiusControl,
              cursor: "pointer",
            }}
          >
            Annuler
          </button>
          {edited && !confirmReset && (
            <button
              type="button"
              className="fbz-press"
              data-testid={`sms-reset-${t.slug}`}
              onClick={onAskReset}
              disabled={saving}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: DS.fontBody,
                fontSize: 13,
                fontWeight: 600,
                padding: "10px 15px",
                borderRadius: DS.radiusControl,
                border: `1px solid ${DS.border}`,
                background: DS.card,
                color: DS.marine,
                cursor: "pointer",
                marginLeft: "auto",
              }}
            >
              <RotateCcw size={15} strokeWidth={2} /> Revenir au texte d'origine
            </button>
          )}
          {edited && confirmReset && (
            <div
              data-testid={`sms-reset-confirmbox-${t.slug}`}
              style={{
                flexBasis: "100%",
                border: `1px solid ${DS.danger}`,
                background: DS.dangerBg,
                borderRadius: DS.radiusControl,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: DS.dangerText,
                  fontWeight: 600,
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  lineHeight: 1.5,
                  marginBottom: 11,
                }}
              >
                <RotateCcw
                  size={15}
                  strokeWidth={2}
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                Ceci efface le corps français édité et ses traductions (EN, ES)
                et remet le texte d'origine. Continuer ?
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="fbz-press"
                  data-testid={`sms-reset-confirm-${t.slug}`}
                  disabled={saving}
                  onClick={onConfirmReset}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    fontFamily: DS.fontBody,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "9px 14px",
                    borderRadius: DS.radiusControl,
                    border: `1px solid ${DS.danger}`,
                    background: DS.danger,
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Oui, revenir au texte d'origine
                </button>
                <button
                  type="button"
                  className="fbz-press"
                  data-testid={`sms-reset-cancel-${t.slug}`}
                  onClick={onCancelReset}
                  style={{
                    border: `1px solid ${DS.border}`,
                    background: DS.card,
                    color: DS.textSecondary,
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: DS.fontBody,
                    padding: "9px 14px",
                    borderRadius: DS.radiusControl,
                    cursor: "pointer",
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Colonne DROITE : aperçu du SMS rendu (pattern PreviewCard de Direct) ─────
// Bulle SMS avec les {variables} développées en valeurs d'exemple + compte de
// caractères (septets GSM-7) / segments du texte d'EXEMPLE rendu — distinct du
// compteur PIRE CAS de l'éditeur (variables à leur longueur max).
function SmsPreviewBubble({ label, text, testId, counterTestId }) {
  const septets = gsm7Septets(text);
  const segments = septets === null ? null : segmentsForSeptets(septets);
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          color: "#7d92a8",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        data-testid={testId}
        style={{
          background: "#e9f1f9",
          color: "#22384e",
          borderRadius: "14px 14px 14px 4px",
          padding: "11px 13px",
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text || "(message vide)"}
      </div>
      <div
        data-testid={counterTestId}
        style={{
          fontSize: 11,
          color: septets === null ? DS.dangerText : DS.textMuted,
          marginTop: 5,
          textAlign: "right",
        }}
      >
        {septets === null
          ? "l'exemple contient un caractère refusé par les SMS"
          : `exemple : ${septets} caractère${septets > 1 ? "s" : ""} · ${segments} SMS facturé${segments > 1 ? "s" : ""}`}
      </div>
    </div>
  );
}

function SmsPreviewCard({ frText, enText, esText, sticky }) {
  const frRendered = interpolateSmsPreview(frText);
  const enRendered = enText && enText.trim() !== "" ? interpolateSmsPreview(enText) : null;
  const esRendered = esText && esText.trim() !== "" ? interpolateSmsPreview(esText) : null;
  return (
    <div
      data-testid="sms-preview"
      style={{
        borderRadius: 20,
        overflow: "hidden",
        boxShadow:
          "0 24px 50px -18px rgba(20,51,80,.42), 0 4px 12px -6px rgba(20,51,80,.2)",
        border: `1px solid rgba(33,83,135,.12)`,
        position: sticky ? "sticky" : "static",
        top: 16,
        background: "#fff",
      }}
    >
      <div
        style={{
          background: `linear-gradient(160deg, ${DS.marine} 0%, ${DS.marine} 55%, ${DS.marineDark} 100%)`,
          color: "#fff",
          padding: "18px 20px 15px",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: DS.ciel,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: DS.marineDeep,
            marginBottom: 12,
            boxShadow: "0 6px 16px -6px rgba(0,0,0,.35)",
          }}
        >
          <Smartphone size={22} strokeWidth={2} />
        </div>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 1,
            color: "#a9cbec",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <MessageSquareText size={13} strokeWidth={2} /> Expéditeur
        </div>
        <div
          style={{
            fontFamily: DS.fontDisplay,
            fontWeight: 600,
            fontSize: 19,
            letterSpacing: -0.2,
            marginTop: 7,
            lineHeight: 1.25,
          }}
        >
          FULLBNBZEN
        </div>
      </div>
      <div style={{ padding: "18px 20px 8px" }}>
        <SmsPreviewBubble
          label="FR"
          text={frRendered}
          testId="sms-preview-fr"
          counterTestId="sms-preview-counter-fr"
        />
        {enRendered !== null && (
          <SmsPreviewBubble
            label="EN"
            text={enRendered}
            testId="sms-preview-en"
            counterTestId="sms-preview-counter-en"
          />
        )}
        {esRendered !== null && (
          <SmsPreviewBubble
            label="ES"
            text={esRendered}
            testId="sms-preview-es"
            counterTestId="sms-preview-counter-es"
          />
        )}
        <div
          style={{
            fontSize: 10.5,
            color: DS.textMuted,
            lineHeight: 1.5,
            padding: "0 0 12px",
          }}
        >
          Les infos du voyageur sont remplacées par des exemples (Camille,
          lien…) ; le modèle enregistré garde les repères {"{…}"}.
        </div>
      </div>
    </div>
  );
}

export default function SmsTransversalView({ cfg }) {
  const isMobile = useIsMobile(); // < 768 : tout empilé (pattern Direct)
  const isNarrow = useIsMobile(1180); // < 1180 : éditeur + aperçu empilés

  // getAllLogsFlat descend dans les chambres sous-loc (lc1/lc2/lc3) — dédup
  // byId (garde KV `fbz8-*` ↔ V2, ADR-051).
  const allLogs = useMemo(
    () => getAllLogsFlat(cfg).map((l) => ({ id: l.id, nom: l.nom })),
    [cfg],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [smsSends, setSmsSends] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState({}); // slug → ligne base

  // Sélection (colonne gauche → éditeur au centre) : 1er gabarit par défaut.
  const [selectedSlug, setSelectedSlug] = useState(
    SMS_CATALOG.length > 0 ? SMS_CATALOG[0].slug : null,
  );
  const selected = SMS_CATALOG.find((t) => t.slug === selectedSlug) || null;
  const dbRow = selected ? smsTemplates[selected.slug] || null : null;
  const edited = !!(
    dbRow &&
    (dbRow.body_fr || dbRow.body_en || dbRow.body_es)
  );

  // Brouillon de l'éditeur — SEUL le FR est éditable (contenu EFFECTIF au
  // montage : base > texte codé). EN + ES sont générés au save (lecture seule).
  const [draftFr, setDraftFr] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [serverIssue, setServerIssue] = useState(null); // {errors, warnings}
  // LOT 5 — carte d'envoi SMS ouverte : null | 'new' | <send id>.
  const [editingSendId, setEditingSendId] = useState(null);

  const smsChannel = INBOX_CHANNELS.find((c) => c.id === "sms") || null;
  const channelEnabled = !!(smsChannel && smsChannel.enabled === true);

  useEffect(() => {
    let aborted = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [sendsRes, logsRes, tplMap] = await Promise.all([
          // LOT 5 — colonnes ÉLARGIES : la carte d'édition a besoin des
          // conditions, du rattrapage et du TEXTE (porté par `messages`, pas
          // par `message_sends`).
          supabase.from("message_sends").select(SMS_SEND_COLUMNS).eq("canal", "sms"),
          // B1 : la colonne d'horodatage de message_logs est `sent_at`
          // (DDL 20260503000005 — created_at N'EXISTE PAS sur cette table),
          // même colonne que JournalTab. m6 : error_message non rendu → non
          // sélectionné.
          supabase
            .from("message_logs")
            .select("id, reservation_id, template_slug, status, sent_at")
            .eq("canal", "sms")
            .order("sent_at", { ascending: false })
            .limit(50),
          // Corps édités (fail-soft : erreur/table absente → {} = textes codés).
          fetchSmsTemplatesMap(),
        ]);
        if (aborted) return;
        if (sendsRes.error) throw sendsRes.error;
        if (logsRes.error) throw logsRes.error;
        setSmsSends((sendsRes.data || []).filter(Boolean));
        setSmsLogs((logsRes.data || []).filter(Boolean));
        setSmsTemplates(tplMap || {});
      } catch (e) {
        if (!aborted) setError(e.message || String(e));
      } finally {
        if (!aborted) setLoading(false);
      }
    }
    load();
    return () => {
      aborted = true;
    };
  }, []);

  // LOT 5 — rechargement CIBLÉ de la liste des envois SMS après un
  // save/suppression (l'écriture passe par l'action serveur ; on relit ce que
  // la base a réellement enregistré, jamais un état optimiste).
  const reloadSends = useCallback(async () => {
    const { data, error: e } = await supabase
      .from("message_sends")
      .select(SMS_SEND_COLUMNS)
      .eq("canal", "sms");
    if (e) {
      setError(e.message || String(e));
      return;
    }
    setSmsSends((data || []).filter(Boolean));
  }, []);

  // (Re)synchronise le brouillon sur le contenu effectif — au changement de
  // gabarit sélectionné ET quand sa ligne base change (chargement initial,
  // save, reset). Après un save la ligne = le brouillon (resync neutre) ;
  // après un reset la ligne disparaît → retour au texte codé.
  useEffect(() => {
    setDraftFr((dbRow && dbRow.body_fr) || (selected ? selected.fr : ""));
    setServerIssue(null);
    setConfirmReset(false);
  }, [selected, dbRow]);

  function handleTemplateSaved(slug, row) {
    setSmsTemplates((prev) => {
      const next = { ...prev };
      if (row) next[slug] = row;
      else delete next[slug];
      return next;
    });
  }

  // ── Validation live du FR (validateur partagé client/serveur) ─────────────
  const vFr = selected ? validateSmsTemplate(selected.slug, draftFr) : null;
  const liveErrors =
    selected && vFr ? vFr.errors.map((e) => ({ ...e, lang: "fr" })) : [];
  const liveWarnings =
    selected && vFr ? vFr.warnings.map((w) => ({ ...w, lang: "fr" })) : [];
  const canSave = !!selected && liveErrors.length === 0 && !saving;

  // ── Traductions générées (lecture seule) : reflet de la ligne en base ─────
  // Un FR édité (dbRow.body_fr) sans traduction pour une langue = échec de
  // traduction → badge « indisponible — fallback ». Tant que rien n'est édité,
  // le texte codé par défaut fait foi (EN codé existe ; ES retombe sur en→fr).
  const isEdited = !!(dbRow && dbRow.body_fr);
  const enGen = dbRow && dbRow.body_en ? dbRow.body_en : null;
  const esGen = dbRow && dbRow.body_es ? dbRow.body_es : null;
  const enBody = enGen || (selected && !isEdited ? selected.en : "");
  const esBody = esGen || "";
  const enShowBadge = isEdited && !enGen;
  const esShowBadge = isEdited && !esGen;

  // Persiste localement le résultat d'un save 'set' : FR + traductions renvoyées
  // par le serveur (chaque langue : { ok, body } ou { ok:false } → NULL en base
  // = fallback à l'envoi). Ne bloque JAMAIS sur un échec de traduction.
  function applySaveResult(resp) {
    const tr = (resp && resp.translations) || {};
    handleTemplateSaved(selected.slug, {
      slug: selected.slug,
      body_fr: draftFr.trim(),
      body_en: tr.en && tr.en.ok ? tr.en.body : null,
      body_es: tr.es && tr.es.ok ? tr.es.body : null,
      updated_at: new Date().toISOString(),
    });
  }

  async function persistSet({ regenerate = false } = {}) {
    setSaving(true);
    setServerIssue(null);
    try {
      const resp = await saveSmsTemplate({
        slug: selected.slug,
        op: "set",
        body_fr: draftFr,
        // « Régénérer » → traduction NON déterministe côté serveur (variante).
        ...(regenerate ? { regenerate: true } : {}),
      });
      applySaveResult(resp);
    } catch (e) {
      // Refus serveur (re-validation FR) : afficher ses erreurs telles quelles.
      if (e.server && e.server.error === "validation_sms") {
        setServerIssue({
          errors: e.server.errors || [],
          warnings: e.server.warnings || [],
        });
      } else {
        setServerIssue({
          errors: [
            {
              code: "save_failed",
              message: e.server?.detail || e.message || "Sauvegarde échouée.",
            },
          ],
          warnings: [],
        });
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!selected || saving) return;
    await persistSet();
  }

  // « Régénérer » : relance la traduction EN+ES sur le FR courant en mode NON
  // déterministe (flag regenerate → bypass cache + température > 0 côté serveur)
  // pour obtenir une VARIANTE — recours quand la traduction auto est fausse.
  async function handleRegenerate() {
    if (!selected || saving || !canSave) return;
    await persistSet({ regenerate: true });
  }

  // Reset : confirmation INLINE (pattern direct-msg-reset-confirmbox — jamais
  // de window.confirm dans les éditeurs Communication → Template).
  async function handleReset() {
    if (!selected || saving) return;
    setSaving(true);
    setServerIssue(null);
    setConfirmReset(false);
    try {
      await saveSmsTemplate({ slug: selected.slug, op: "reset" });
      handleTemplateSaved(selected.slug, null);
    } catch (e) {
      setServerIssue({
        errors: [
          {
            code: "reset_failed",
            message: e.message || "Le retour au texte d'origine a échoué.",
          },
        ],
        warnings: [],
      });
    } finally {
      setSaving(false);
    }
  }

  // Annuler : retour du brouillon FR au contenu effectif (base > texte codé).
  function handleCancel() {
    setDraftFr((dbRow && dbRow.body_fr) || (selected ? selected.fr : ""));
    setServerIssue(null);
    setConfirmReset(false);
  }

  return (
    <div data-testid="sms-transversal-view" style={{ fontFamily: DS.fontBody }}>
      {/* ── Les 3 colonnes (disposition EXACTE de l'onglet Direct) ── */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
          alignItems: "flex-start",
        }}
      >
        {/* ── Colonne gauche : état du canal + liste des gabarits ── */}
        <div
          style={{
            width: isMobile ? "100%" : 360,
            flexShrink: 0,
            background: DS.card,
            border: `0.5px solid ${DS.border}`,
            borderRadius: DS.radiusCard,
            boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
            overflow: "hidden",
          }}
        >
          {/* 1. État du canal (état ACTUEL, règle « textes d'aide ») */}
          <div
            data-testid="sms-channel-state"
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              padding: "12px 14px",
              background: channelEnabled ? DS.successBg : DS.warningBg,
              color: channelEnabled ? DS.successText : DS.warningText,
              borderBottom: `1px solid ${DS.border}`,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {channelEnabled ? (
              <Power
                size={16}
                strokeWidth={2}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
            ) : (
              <PowerOff
                size={16}
                strokeWidth={2}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
            )}
            <div>
              <strong>
                SMS : {channelEnabled ? "ouverts" : "pas encore ouverts"}
              </strong>
              {!channelEnabled && (
                <>
                  {" "}
                  — aucun SMS ne part aujourd'hui ; l'ouverture se fera sur
                  votre décision. Les SMS partiront au nom « FULLBNBZEN » et le
                  voyageur ne pourra pas y répondre. Modifier un texte
                  ci-dessous n'envoie rien.
                </>
              )}
              {/* État RÉEL du chemin `message_sends` canal SMS : trois verrous
                  DISTINCTS de celui ci-dessus (qui, lui, ne pilote que le
                  parcours Direct). Règle « textes d'aide = état actuel ». */}
              <div style={{ marginTop: 7 }}>
                Les <strong>envois Plateformes en SMS</strong> (plus bas) ont
                leurs propres verrous, aucun pilotable depuis cet écran :
                <ul style={{ margin: "4px 0 0", paddingLeft: 17 }}>
                  <li>
                    l'interrupteur SMS de l'application (réglé côté technique)
                    — coupé, rien ne part ;
                  </li>
                  <li>
                    l'essai sans envoi, pour tous les messages — les envois sont
                    simulés et notés au journal, rien ne part ;
                  </li>
                  <li>
                    l'accord du voyageur pour recevoir des SMS — sans son
                    accord, pas de SMS.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 2. Liste des gabarits (sélection → éditeur au centre) */}
          <div
            data-testid="sms-gabarit-rail"
            style={{
              padding: "8px 8px 12px",
              maxHeight: isMobile ? "none" : "calc(100vh - 150px)",
              overflowY: isMobile ? "visible" : "auto",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 1.1,
                textTransform: "uppercase",
                color: DS.marineDeep,
                padding: "4px 6px 6px",
              }}
            >
              Modèles de SMS ({SMS_CATALOG.length}) — texte d'origine tant que
              vous ne l'avez pas modifié
            </div>
            {SMS_CATALOG.map((t) => {
              const row = smsTemplates[t.slug] || null;
              return (
                <SmsTemplateRow
                  key={t.slug}
                  t={t}
                  edited={!!(row && (row.body_fr || row.body_en || row.body_es))}
                  selected={t.slug === selectedSlug}
                  onSelect={() => setSelectedSlug(t.slug)}
                />
              );
            })}
          </div>
        </div>

        {/* ── Zone droite : éditeur + aperçu (pattern Direct) ── */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            width: isMobile ? "100%" : "auto",
            display: "flex",
            flexDirection: isNarrow ? "column" : "row",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              flex: "1 1 0",
              minWidth: 0,
              width: isNarrow ? "100%" : "auto",
            }}
          >
            {selected ? (
              <SmsEditorCard
                t={selected}
                edited={edited}
                draftFr={draftFr}
                onChangeFr={setDraftFr}
                vFr={vFr}
                enBody={enBody}
                esBody={esBody}
                enShowBadge={enShowBadge}
                esShowBadge={esShowBadge}
                onRegenerate={handleRegenerate}
                regenerating={saving}
                liveErrors={liveErrors}
                liveWarnings={liveWarnings}
                serverIssue={serverIssue}
                canSave={canSave}
                saving={saving}
                confirmReset={confirmReset}
                onSave={handleSave}
                onCancel={handleCancel}
                onAskReset={() => setConfirmReset(true)}
                onCancelReset={() => setConfirmReset(false)}
                onConfirmReset={handleReset}
              />
            ) : (
              <div style={{ color: DS.textMuted, padding: 20 }}>
                Sélectionnez un gabarit à gauche.
              </div>
            )}
          </div>

          <div
            style={{
              flex: "1 1 0",
              minWidth: 0,
              width: isNarrow ? "100%" : "auto",
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: DS.marine,
                marginBottom: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Eye size={13} strokeWidth={2} /> Ce que reçoit le voyageur
            </div>
            {selected && (
              <SmsPreviewCard
                frText={draftFr}
                enText={enGen || selected.en}
                esText={esGen || null}
                sticky={!isNarrow}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── 3. Envois programmés Plateformes canal SMS (sous les 3 colonnes,
             comme Direct dont la colonne gauche ne porte que la liste) ── */}
      <SectionTitle>Envois programmés Plateformes — canal SMS</SectionTitle>
      {loading && (
        <div style={{ fontSize: 12, color: DS.textSecondary }}>Chargement…</div>
      )}
      {error && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "9px 12px",
            background: DS.dangerBg,
            color: DS.dangerText,
            borderRadius: 8,
            fontSize: 12.5,
          }}
        >
          <AlertTriangle size={14} strokeWidth={2} /> {error}
        </div>
      )}
      {/* Aide — état ACTUEL du chemin message_sends (règle « textes d'aide »). */}
      {!loading && !error && (
        <div
          data-testid="sms-sends-help"
          style={{
            fontSize: 11.5,
            color: DS.textMuted,
            lineHeight: 1.5,
            marginBottom: 8,
          }}
        >
          Le texte est vérifié quand vous enregistrez : caractères acceptés par
          les SMS, 160 caractères au plus même avec les infos les plus longues,
          seulement les infos et paragraphes prévus. Si la vérification échoue,
          rien n'est enregistré.
        </div>
      )}

      {!loading && !error && smsSends.length === 0 && editingSendId !== "new" && (
        <div
          data-testid="sms-sends-empty"
          style={{ fontSize: 12.5, color: DS.textMuted, marginBottom: 8 }}
        >
          Aucun envoi Plateformes n'utilise le canal SMS pour l'instant.
        </div>
      )}

      {!loading &&
        !error &&
        smsSends.map((s) =>
          editingSendId === s.id ? (
            <SmsSendCard
              key={s.id}
              allLogs={allLogs}
              initial={s}
              onSaved={async () => {
                setEditingSendId(null);
                await reloadSends();
              }}
              onCancel={() => setEditingSendId(null)}
            />
          ) : (
            <div
              key={s.id}
              data-testid={`sms-send-${s.id}`}
              role="button"
              tabIndex={0}
              className="fbz-rowhover"
              onClick={() => setEditingSendId(s.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setEditingSendId(s.id);
                }
              }}
              style={{
                background: DS.card,
                border: `0.5px solid ${DS.border}`,
                borderRadius: DS.radiusCard,
                padding: "10px 14px",
                marginBottom: 6,
                fontSize: 12.5,
                color: DS.textSecondary,
                cursor: "pointer",
                opacity: isEngineActiveStatus(s.status) ? 1 : 0.9,
              }}
            >
              <strong style={{ color: DS.marineDark }}>
                {s.messages?.name || s.messages?.slug || s.message_id}
              </strong>
              <div style={{ marginTop: 4, lineHeight: 1.5 }}>
                {humanSendSummary(s, allLogs.length)}
              </div>
            </div>
          ),
        )}

      {editingSendId === "new" && (
        <SmsSendCard
          allLogs={allLogs}
          initial={null}
          onSaved={async () => {
            setEditingSendId(null);
            await reloadSends();
          }}
          onCancel={() => setEditingSendId(null)}
        />
      )}

      {!loading && !error && editingSendId === null && (
        <button
          type="button"
          className="fbz-press"
          data-testid="sms-send-new"
          onClick={() => setEditingSendId("new")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginTop: 4,
            background: "transparent",
            border: `0.5px dashed ${DS.marine}`,
            color: DS.marine,
            borderRadius: 8,
            padding: "7px 13px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          <Plus size={13} strokeWidth={2.4} /> Nouvel envoi SMS
        </button>
      )}

      {/* ── 4. Journal des envois SMS (50 derniers, zéro PII) ── */}
      <SectionTitle>Journal des envois SMS (50 derniers)</SectionTitle>
      {!loading && !error && smsLogs.length === 0 && (
        <div
          data-testid="sms-logs-empty"
          style={{ fontSize: 12.5, color: DS.textMuted }}
        >
          Aucun SMS envoyé pour l'instant (les SMS ne sont pas encore ouverts).
        </div>
      )}
      {!loading && !error && smsLogs.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            data-testid="sms-logs-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12,
              color: DS.textSecondary,
            }}
          >
            <thead>
              <tr style={{ textAlign: "left", color: DS.textMuted }}>
                <th style={{ padding: "6px 8px" }}>Date</th>
                <th style={{ padding: "6px 8px" }}>SMS</th>
                <th style={{ padding: "6px 8px" }}>Statut</th>
                <th style={{ padding: "6px 8px" }}>Réservation</th>
              </tr>
            </thead>
            <tbody>
              {smsLogs.map((l) => (
                <tr key={l.id} style={{ borderTop: `1px solid ${DS.border}` }}>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    {l.sent_at
                      ? new Date(l.sent_at).toLocaleString("fr-FR")
                      : "—"}
                  </td>
                  <td
                    style={{ padding: "6px 8px" }}
                    title={l.template_slug || undefined}
                  >
                    {smsTemplateName(l.template_slug)}
                  </td>
                  <td style={{ padding: "6px 8px" }} title={l.status}>
                    {sendStatusLabel(l.status)}
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <code style={{ fontSize: 11 }}>
                      {l.reservation_id
                        ? String(l.reservation_id).slice(0, 8) + "…"
                        : "—"}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
